<template>
    <div class="chat-message-list" ref="messageList" @scroll="handleScroll">
        <!-- Load more indicator -->
        <div v-if="loading && hasMore" class="load-more-indicator">
            <div class="spinner"></div>
            <span>Chargement...</span>
        </div>

        <!-- Load more button -->
        <button
            v-if="hasMore && !loading"
            class="load-more-btn"
            @click="$emit('load-more')"
        >
            Charger les messages précédents
        </button>

        <!-- Messages -->
        <div class="messages-container">
            <template v-for="(message, index) in messages">
                <!-- Date separator -->
                <div
                    v-if="shouldShowDateSeparator(index)"
                    :key="'date-' + message._id"
                    class="date-separator"
                >
                    <span>{{ formatDateSeparator(message.createdAt) }}</span>
                </div>

                <!-- Message -->
                <ChatMessage
                    :key="message._id || message.tempId"
                    :message="message"
                    :current-user-id="currentUserId"
                    :show-avatar="shouldShowAvatar(index)"
                    :show-sender-name="shouldShowSenderName(index)"
                />
            </template>
        </div>

        <!-- Typing indicator -->
        <ChatTypingIndicator
            v-if="typingUsers && typingUsers.length > 0"
            :is-typing="true"
            :user-name="typingUserName"
        />

        <!-- Empty state -->
        <div v-if="!loading && messages.length === 0" class="empty-state">
            <i class="material-icons">chat_bubble_outline</i>
            <p>Aucun message pour le moment</p>
            <span>Commencez la conversation!</span>
        </div>
    </div>
</template>

<script>
import ChatMessage from './ChatMessage.vue';
import ChatTypingIndicator from './ChatTypingIndicator.vue';

export default {
    name: 'ChatMessageList',
    components: {
        ChatMessage,
        ChatTypingIndicator
    },
    props: {
        messages: {
            type: Array,
            default: () => []
        },
        currentUserId: {
            type: String,
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        },
        hasMore: {
            type: Boolean,
            default: false
        },
        typingUsers: {
            type: Array,
            default: () => []
        },
        participantName: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            isAtBottom: true,
            prevScrollHeight: 0
        };
    },
    computed: {
        typingUserName() {
            // For now, just show the participant name
            return this.participantName;
        }
    },
    watch: {
        messages: {
            handler(newMessages, oldMessages) {
                const wasAtBottom = this.isAtBottom;
                const hadMessages = oldMessages && oldMessages.length > 0;
                const hasNewMessage = newMessages.length > ((oldMessages && oldMessages.length) || 0);

                this.$nextTick(() => {
                    if (!hadMessages || (wasAtBottom && hasNewMessage)) {
                        this.scrollToBottom();
                    } else if (hasNewMessage && !wasAtBottom) {
                        // Maintain scroll position when loading older messages
                        const list = this.$refs.messageList;
                        if (list) {
                            const newScrollHeight = list.scrollHeight;
                            list.scrollTop = newScrollHeight - this.prevScrollHeight;
                        }
                    }
                });
            },
            deep: true
        }
    },
    mounted() {
        this.scrollToBottom();
    },
    methods: {
        shouldShowDateSeparator(index) {
            if (index === 0) return true;

            const currentDate = new Date(this.messages[index].createdAt).toDateString();
            const prevDate = new Date(this.messages[index - 1].createdAt).toDateString();

            return currentDate !== prevDate;
        },
        shouldShowAvatar(index) {
            // Show avatar for first message or when sender changes
            if (index === 0) return true;

            const currentMsg = this.messages[index];
            const prevMsg = this.messages[index - 1];
            const currentSender = (currentMsg.sender && currentMsg.sender._id) || currentMsg.sender;
            const prevSender = (prevMsg.sender && prevMsg.sender._id) || prevMsg.sender;

            return currentSender !== prevSender;
        },
        shouldShowSenderName(index) {
            return this.shouldShowAvatar(index);
        },
        formatDateSeparator(dateString) {
            const date = new Date(dateString);
            const today = new Date();
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);

            if (date.toDateString() === today.toDateString()) {
                return "Aujourd'hui";
            } else if (date.toDateString() === yesterday.toDateString()) {
                return 'Hier';
            } else {
                return date.toLocaleDateString('fr-FR', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long'
                });
            }
        },
        handleScroll() {
            const list = this.$refs.messageList;
            if (!list) return;

            // Check if at bottom
            const threshold = 50;
            this.isAtBottom = list.scrollHeight - list.scrollTop - list.clientHeight < threshold;

            // Store scroll height for maintaining position
            this.prevScrollHeight = list.scrollHeight;

            // Check if at top for loading more
            if (list.scrollTop < 50 && this.hasMore && !this.loading) {
                this.$emit('load-more');
            }
        },
        scrollToBottom(smooth = false) {
            this.$nextTick(() => {
                const list = this.$refs.messageList;
                if (list) {
                    if (smooth) {
                        list.scrollTo({
                            top: list.scrollHeight,
                            behavior: 'smooth'
                        });
                    } else {
                        list.scrollTop = list.scrollHeight;
                    }
                    this.isAtBottom = true;
                }
            });
        }
    }
};
</script>

<style scoped>
.chat-message-list {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.load-more-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px;
    color: #6b7280;
    font-size: 13px;
}

.spinner {
    width: 16px;
    height: 16px;
    border: 2px solid #e5e7eb;
    border-top-color: #059669;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.load-more-btn {
    align-self: center;
    padding: 8px 16px;
    background-color: #f3f4f6;
    border: none;
    border-radius: 16px;
    color: #6b7280;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s;
    margin-bottom: 8px;
}

.load-more-btn:hover {
    background-color: #e5e7eb;
    color: #374151;
}

.messages-container {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.date-separator {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 16px 0;
}

.date-separator span {
    padding: 4px 12px;
    background-color: #f3f4f6;
    border-radius: 12px;
    color: #6b7280;
    font-size: 12px;
    font-weight: 500;
}

.empty-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
    text-align: center;
    padding: 40px 20px;
}

.empty-state i {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
}

.empty-state p {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    color: #6b7280;
}

.empty-state span {
    font-size: 14px;
    margin-top: 4px;
}
</style>
